<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <app-cells>
        <app-button @click.native="onShowModal(null)">Create GPS</app-button>
      </app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
        :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__name><div class="table-default__left">Name</div></template>
        <template #h__shared_link><div class="table-default__left">Shared link</div></template>
        <template #h__edit_link><div class="table-default__left">Edit link</div></template>
        <template #name="{ row }"><div class="table-default__left">{{ row.name }}</div></template>
        <template #shared_link="{ row }">
          <a
            :href="row.shared_link"
            target="_blank"
            class="table-default__link-border"
          >
            {{ row.shared_link }}
          </a>
        </template>
        <template #edit_link="{ row }">
          <a
            :href="row.edit_link"
            target="_blank"
            class="table-default__link-border"
          >
            {{ row.edit_link }}
          </a>
        </template>
        <template #controls="{ row }">
          <div class="table-default__controls">
            <button @click="onShowModal(row)" type="button"><img src="@/assets/img/edit-table-icon.svg" alt="Edit"></button>
          </div>
        </template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
            :clickHandler="onClickPagination"
            v-if="pagination.pages > 1"
            v-model="pagination.page"
            :page-count="pagination.pages"
            :prev-text="'<'"
            :next-text="'>'"
            :container-class="'pagination'"
            :page-class="'pagination__item'"
            :page-link-class="'pagination__link'"
            :prev-class="'pagination__item pagination__item--prev'"
            :next-class="'pagination__item pagination__item--next'"
            :next-link-class="'pagination__link pagination__link--next'"
            :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" />
      </app-cells>
    </template>
    <modal
      name="gps-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="onHideModal" class="popup__close" type="button"></button>
        <div class="popup__title">{{ edit_gps ? 'Update' : 'Create' }} GPS</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Name" label-for="name" required>
              <app-input
                v-model="form.name"
                placeholder="Name"
                id="name"
                name="name"
                :error="$v.form.name.$error"
              />
              <template #error>
                <div v-if="$v.form.name.$dirty && !$v.form.name.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Number" label-for="number" required>
              <app-input
                v-model="form.number"
                placeholder="Number"
                id="number"
                name="number"
                :error="$v.form.number.$error"
              />
              <template #error>
                <div v-if="$v.form.number.$dirty && !$v.form.number.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Shared link" label-for="shared_link" required>
              <app-input
                v-model="form.shared_link"
                placeholder="Shared link"
                id="shared_link"
                name="shared_link"
                type="url"
                :error="$v.form.shared_link.$error"
              />
              <template #error>
                <div v-if="$v.form.shared_link.$dirty && !$v.form.shared_link.required">Required field</div>
                <div v-if="$v.form.edit_link.$dirty && $v.form.shared_link.$invalid">Wrong link</div>
              </template>
            </app-form-group>
            <app-form-group label="Edit link" label-for="edit_link" required>
              <app-input
                v-model="form.edit_link"
                placeholder="Edit link"
                id="edit_link"
                name="edit_link"
                type="url"
                :error="$v.form.edit_link.$error"
              />
              <template #error>
                <div v-if="$v.form.edit_link.$dirty && !$v.form.edit_link.required">Required field</div>
                <div v-if="$v.form.edit_link.$dirty && $v.form.edit_link.$invalid">Wrong link</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button ref="submit" type="submit">{{ edit_gps ? 'Save' : 'Create' }}</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { getClimbingGPSList, patchClimbingGPS, postClimbingGPS } from '@/http'
import { required, url } from 'vuelidate/lib/validators'

export default {
  name: 'ClimbingSettingsGPS',
  data() {
    return {
      is_loaded: false,
      table_preloader: false,
      edit_gps: true,
      form: {},
      table: {
        items: [],
        columns: [
          'name',
          'number',
          'shared_link',
          'edit_link',
          'controls'
        ],
        options: {
          headings: {
            'name': 'Name',
            'number': 'Number ',
            'shared_link': 'Shared link',
            'edit_link': 'Edit link',
            'controls': '',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
    }
  },
  validations: {
    form: {
      name: { required },
      number: { required },
      shared_link: { required, url },
      edit_link: { required, url },
    },
  },
  created() {
    this.fetchItems()
  },
  methods: {
    async fetchItems() {
      const response = await getClimbingGPSList({ page: this.pagination.page, page_size: 20 })
      this.table.items = response.data.results
      this.pagination.pages = response.data.pages
      this.pagination.count = response.data.count
      this.is_loaded = true
      this.table_preloader = false
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      if (!this.edit_gps) {
        postClimbingGPS(this.form)
          .then(() => {
            this.table_preloader = true
            this.fetchItems()
            this.onHideModal()
          })
          .catch(() => { this.$refs.submit.preload = false })
      } else {
        patchClimbingGPS(this.form.id, this.form)
          .then(() => {
            this.table_preloader = true
            this.fetchItems()
            this.onHideModal()
          })
          .catch(() => { this.$refs.submit.preload = false })
      }
    },
    onShowModal(data) {
      this.edit_gps = !!data
      if (!this.edit_gps) this.form = {}
      else {
        this.form = data
      }
      this.$v.form.$reset()
      this.$modal.show('gps-modal')
    },
    onHideModal() {
      this.$modal.hide('gps-modal')
    },
    onClickPagination(page) {
      this.table_preloader = true
      this.pagination.page = page
      this.fetchItems()
    },
  }
}
</script>
